import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  IconButton,
  Box,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  AttachFile as AttachFileIcon
} from '@mui/icons-material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import Divider from '@mui/material/Divider';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';
import { catalogosService } from '../../services/catalogosService';
import { Ramo } from '../../types';
import { useSnackbar } from 'notistack';
// import productoService from '../../services/productoService'; // ya no se usa aquí
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';

const Ramos: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [ramos, setRamos] = useState<Ramo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [openImportar, setOpenImportar] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Columnas para la plantilla de Ramos (según API /api/Catalogos/ramos)
  const columnasRamo = [
    'codigoRamo',
    'nombreRamo',
    'estadoRamo',
    'ctaInventario',
    'ctaVenta',
    'ctaCompra',
    'ctaCostoVenta',
    'ctaDesc',
    'ctaCtraPartida',
    'archCodBarra',
    'cantDecimales',
    'editaPrecio',
    'articuloXlistaPrecios'
  ];
  const camposObligatoriosRamo = ['codigoRamo', 'nombreRamo', 'ctaInventario', 'ctaVenta'];
  const camposNumericosRamo = [
    'ctaInventario','ctaVenta','ctaCompra','ctaCostoVenta','ctaDesc','ctaCtraPartida','cantDecimales','editaPrecio'
  ];
  // Nota: la importación de ramos se implementará más adelante; por ahora solo validamos estructura y generamos plantilla.


  // Descargar plantilla de Ramos
  const handleDescargarPlantilla = () => {
    try {
      const data = [columnasRamo, new Array(columnasRamo.length).fill('')];
      const ws = XLSX.utils.aoa_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Ramos');
      // Hoja de instrucciones con valores aceptados
      const instrucciones = [
        ['Columna', 'Descripción', 'Valores aceptados'],
        ['codigoRamo', 'Código único del ramo', 'Texto alfanumérico (obligatorio)'],
        ['nombreRamo', 'Nombre del ramo', 'Texto (obligatorio)'],
        ['estadoRamo', 'Estado del ramo', 'Activo o Inactivo (opcional)'],
        ['ctaInventario', 'Cuenta inventario', 'Número (obligatorio)'],
        ['ctaVenta', 'Cuenta venta', 'Número (obligatorio)'],
        ['ctaCompra', 'Cuenta compra', 'Número (opcional)'],
        ['ctaCostoVenta', 'Cuenta costo venta', 'Número (opcional)'],
        ['ctaDesc', 'Cuenta descuento', 'Número (opcional)'],
        ['ctaCtraPartida', 'Cuenta contrapartida', 'Número (opcional)'],
        ['archCodBarra', 'Archivo código de barras', 'Texto (opcional)'],
        ['cantDecimales', 'Cantidad decimales', 'Número (opcional)'],
        ['editaPrecio', 'Editar precio', 'Número (opcional)'],
        ['articuloXlistaPrecios', 'Artículos por lista de precio', 'JSON array (opcional, dejar vacío si no aplica)'],
      ];
      const wsInstrucciones = XLSX.utils.aoa_to_sheet(instrucciones);
      XLSX.utils.book_append_sheet(wb, wsInstrucciones, 'Instrucciones');
      XLSX.writeFile(wb, 'PlantillaRamos.xlsx');
      enqueueSnackbar('✅ Plantilla Excel descargada correctamente', { variant: 'success', autoHideDuration: 4000 });
    } catch (error) {
      enqueueSnackbar('❌ Error al crear la plantilla Excel', { variant: 'error', autoHideDuration: 4000 });
    }
  };

  const onImportChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows: any[] = XLSX.utils.sheet_to_json(sheet, { defval: '', header: 0 });
      enqueueSnackbar('Archivo leído, procesando filas.', { variant: 'info', autoHideDuration: 3000 });
      if (!rows.length) {
        enqueueSnackbar('El archivo está vacío.', { variant: 'warning', autoHideDuration: 4000 });
        return;
      }
      const headerAliases: Record<string, string> = { codProveedor: 'codigoProveedor' };
      const normalizedRows = rows.map((r) => {
        const copy: any = { ...r };
        Object.entries(headerAliases).forEach(([from, to]) => {
          if (copy[from] !== undefined && copy[to] === undefined) {
            copy[to] = copy[from];
          }
        });
        return copy;
      });
      const headersRaw = Object.keys(normalizedRows[0]);
      const headersNorm = new Map(headersRaw.map((h: string) => [h.trim().toLowerCase(), h]));
      const noEncontrados = columnasRamo.filter((h: string) => !headersNorm.has(h.toLowerCase()));
      if (noEncontrados.length > 0) {
        enqueueSnackbar('Columnas faltantes: ' + noEncontrados.join(', '), { variant: 'error', autoHideDuration: 5000 });
        return;
      }
      const getVal = (r: any, campo: string) => {
        const k = headersNorm.get(campo.toLowerCase()) ?? campo;
        return r[k];
      };
      // Pre-cargar códigos existentes para evitar duplicados
      const existing = await catalogosService.getRamos(1, 10000, '');
      const existeCodigoRamo = new Set((existing.items || []).map((r: any) => String(r.codigoRamo).trim()));

      let exitos = 0, fallos = 0;
      for (let i = 0; i < normalizedRows.length; i++) {
        const row = normalizedRows[i] as any;
        const rowNorm: Record<string, any> = {};
        columnasRamo.forEach((c: string) => { rowNorm[c] = getVal(row, c); });

        // Validar obligatorios
        const faltan: string[] = [];
        for (const campo of camposObligatoriosRamo) {
          if (rowNorm[campo] === undefined || rowNorm[campo] === null || String(rowNorm[campo]).trim() === '') {
            faltan.push(campo);
          }
        }
        if (faltan.length > 0) {
          enqueueSnackbar(`❌ Fila ${i + 2}: Falta completar: ${faltan.join(', ')}`, { variant: 'error', autoHideDuration: 8000 });
          fallos++; continue;
        }

        // Validar duplicado
        const codigo = String(rowNorm.codigoRamo).trim();
        if (existeCodigoRamo.has(codigo)) {
          enqueueSnackbar(`🔴 Ramo duplicado (codigoRamo: ${codigo})`, { variant: 'error', autoHideDuration: 8000 });
          fallos++; continue;
        }

        // Validar numéricos (Excel puede devolver números como number)
        const tipoMal: string[] = [];
        for (const nCampo of camposNumericosRamo) {
          const val = rowNorm[nCampo];
          if (val !== '' && val !== undefined && val !== null && isNaN(Number(val))) {
            tipoMal.push(nCampo);
          }
        }
        if (tipoMal.length > 0) {
          enqueueSnackbar(`❌ Fila ${i + 2}: campo inválido: ${tipoMal.join(', ')} (debe ser número)`, { variant: 'error', autoHideDuration: 9000 });
          fallos++; continue;
        }

        // Construir objeto para API (String/Number convierten correctamente desde Excel)
        const ramo: any = {
          codigoRamo: codigo,
          nombreRamo: String(rowNorm.nombreRamo).trim(),
        };
        if (rowNorm.estadoRamo !== undefined && String(rowNorm.estadoRamo).trim() !== '') {
          ramo.estadoRamo = String(rowNorm.estadoRamo).trim();
        } else {
          ramo.estadoRamo = null;
        }
        // Campos string opcionales
        if (rowNorm.archCodBarra !== undefined && rowNorm.archCodBarra !== '') {
          ramo.archCodBarra = String(rowNorm.archCodBarra);
        }
        // Campos numéricos opcionales
        for (const key of camposNumericosRamo) {
          const v = rowNorm[key];
          if (v !== '' && v !== undefined && v !== null) {
            ramo[key] = Number(v);
          }
        }

        // Array: articuloXlistaPrecios (debe ser un JSON válido de array)
        if (rowNorm.articuloXlistaPrecios !== undefined && rowNorm.articuloXlistaPrecios !== '') {
          try {
            const raw = rowNorm.articuloXlistaPrecios;
            const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
            if (Array.isArray(parsed)) {
              ramo.articuloXlistaPrecios = parsed;
            } else {
              enqueueSnackbar(`❌ Fila ${i + 2}: articuloXlistaPrecios debe ser un JSON de array`, { variant: 'error', autoHideDuration: 8000 });
              fallos++; continue;
            }
          } catch {
            enqueueSnackbar(`❌ Fila ${i + 2}: articuloXlistaPrecios no es un JSON válido`, { variant: 'error', autoHideDuration: 8000 });
            fallos++; continue;
          }
        }

        try {
          await catalogosService.createRamo(ramo);
          exitos++;
          // Agregar al set para evitar duplicados dentro del mismo archivo
          existeCodigoRamo.add(codigo);
        } catch (err: any) {
          let mensajePlano = err?.response?.data?.message || err?.response?.data || err?.message || 'Error en backend';
          if (err?.response?.data?.errors) {
            const errores = err.response.data.errors;
            let detalle = '';
            for (let campo in errores) {
              if (Array.isArray(errores[campo])) {
                detalle += `${campo}: ${errores[campo].join('; ')}\n`;
              }
            }
            enqueueSnackbar('❌ Error en los datos: ' + detalle, { variant: 'error', autoHideDuration: 12000 });
            fallos++; continue;
          }
          if (typeof mensajePlano !== 'string') { try { mensajePlano = JSON.stringify(mensajePlano); } catch {} }
          enqueueSnackbar(`❌ Fila ${i+2}: Error al guardar: ${String(mensajePlano).slice(0,120)}`, { variant: 'error', autoHideDuration: 9000 });
          fallos++;
        }
      }

      enqueueSnackbar(`Importación de ramos completada. Éxitos: ${exitos}, Fallos: ${fallos}`, { variant: fallos > 0 ? 'error' : 'success', autoHideDuration: 4500 });
      fetchRamos(page, rowsPerPage, searchTerm);
      setTimeout(() => { setOpenImportar(false); }, 800);
    } catch (error) {
      enqueueSnackbar('Error leyendo el archivo Excel (¿archivo corrupto/formato incorrecto?)', { variant: 'error', autoHideDuration: 9000 });
    }
  };

  const fetchRamos = useCallback(async (pageNumber: number, pageSize: number, search?: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await catalogosService.getRamos(pageNumber + 1, pageSize, search);
      console.log('Datos de ramos:', response.items);
      if (response && response.items) {
        setRamos(response.items);
        setTotalCount(response.totalCount);
      } else {
        setRamos([]);
        setTotalCount(0);
        setError('No se pudo cargar la información de ramos');
      }
    } catch (error) {
      console.error('Error al cargar ramos:', error);
      setRamos([]);
      setTotalCount(0);
      setError('Error al cargar los ramos');
      enqueueSnackbar('Error al cargar los ramos', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchRamos(page, rowsPerPage, searchTerm);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [page, rowsPerPage, searchTerm, fetchRamos]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleDelete = async (codigoRamo: string) => {
    if (window.confirm('¿Está seguro de que desea eliminar este ramo?')) {
      try {
        await catalogosService.deleteRamo(codigoRamo);
        enqueueSnackbar('Ramo eliminado con éxito', { variant: 'success' });
        fetchRamos(page, rowsPerPage, searchTerm);
      } catch (error) {
        console.error('Error al eliminar ramo:', error);
        enqueueSnackbar('Error al eliminar el ramo', { variant: 'error' });
      }
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card 
        sx={{ 
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack 
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between" 
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600,
                color: '#1976d2'
              }}
            >
              Ramos
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => navigate('/inventario/ramos/nuevo')}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: '0 2px 4px rgba(21, 128, 61, 0.3)',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(21, 128, 61, 0.4)',
                  }
                }}
              >
                Nuevo Ramo
              </Button>
              <Button
                variant="outlined"
                startIcon={<AttachFileIcon />}
                onClick={() => setOpenImportar(true)}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Importar ramos
              </Button>
            </Stack>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <TextField
              label="Buscar"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => {setSearchTerm(e.target.value);}}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: '#fafafa',
                  '&:hover': {
                    bgcolor: '#f5f5f5',
                  },
                  '&.Mui-focused': {
                    bgcolor: '#ffffff',
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#666' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {isMobile ? (
            <Stack spacing={2}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Typography sx={{ color: 'error.main', textAlign: 'center', py: 2 }}>{error}</Typography>
              ) : ramos.length === 0 ? (
                <Typography sx={{ textAlign: 'center', py: 2 }}>No se encontraron ramos</Typography>
              ) : (
                ramos.map((ramo) => (
                  <Card 
                    key={ramo.codigoRamo}
                    sx={{
                      borderRadius: 2,
                      border: '1px solid #e0e0e0',
                      '&:hover': {
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    <CardContent>
                      <Stack spacing={1.5}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {ramo.nombreRamo.trim()}
                          </Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Código:</strong> {ramo.codigoRamo.trim()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Estado:</strong> {ramo.estadoRamo?.trim().toUpperCase() === 'A' ? 'Activo' : 'Inactivo'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Cuenta Inventario:</strong> {ramo.ctaInventario}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Cuenta Venta:</strong> {ramo.ctaVenta}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                          <Tooltip title="Editar">
                            <IconButton
                              onClick={() => navigate(`/inventario/ramos/${ramo.codigoRamo.trim()}`)}
                              size="small"
                              sx={{
                                bgcolor: '#2196f3',
                                color: '#fff',
                                borderRadius: 1.5,
                                '&:hover': { bgcolor: '#1565c0' }
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar">
                            <IconButton
                              onClick={() => handleDelete(ramo.codigoRamo.trim())}
                              size="small"
                              sx={{
                                bgcolor: '#f44336',
                                color: '#fff',
                                borderRadius: 1.5,
                                '&:hover': { bgcolor: '#d32f2f' }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                ))
              )}
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
                labelDisplayedRows={({ from, to, count }) => 
                  `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
                }
              />
            </Stack>
          ) : (
            <TableContainer 
              component={Paper}
              sx={{
                borderRadius: 2,
                boxShadow: 'none',
                border: '1px solid #e0e0e0',
              }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Nombre</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Estado</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Cuenta Inventario</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Cuenta Venta</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : error ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ color: 'error.main' }}>
                        {error}
                      </TableCell>
                    </TableRow>
                  ) : ramos.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No se encontraron ramos
                      </TableCell>
                    </TableRow>
                  ) : (
                    ramos.map((ramo) => (
                      <TableRow 
                        key={ramo.codigoRamo}
                        sx={{
                          '&:hover': {
                            bgcolor: '#f9f9f9',
                          },
                        }}
                      >
                        <TableCell sx={{ fontWeight: 500 }}>{ramo.codigoRamo.trim()}</TableCell>
                        <TableCell>{ramo.nombreRamo.trim()}</TableCell>
                        <TableCell>{ramo.estadoRamo?.trim().toUpperCase() === 'A' ? 'Activo' : 'Inactivo'}</TableCell>
                        <TableCell>{ramo.ctaInventario}</TableCell>
                        <TableCell>{ramo.ctaVenta}</TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            <Tooltip title="Editar">
                              <IconButton
                                onClick={() => navigate(`/inventario/ramos/${ramo.codigoRamo.trim()}`)}
                                size="small"
                                sx={{
                                  bgcolor: '#2196f3',
                                  color: '#fff',
                                  borderRadius: 1.5,
                                  width: 32,
                                  height: 32,
                                  '&:hover': { 
                                    bgcolor: '#1976d2',
                                    transform: 'scale(1.1)',
                                  },
                                  transition: 'all 0.2s'
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Eliminar">
                              <IconButton
                                onClick={() => handleDelete(ramo.codigoRamo.trim())}
                                size="small"
                                sx={{
                                  bgcolor: '#f44336',
                                  color: '#fff',
                                  borderRadius: 1.5,
                                  width: 32,
                                  height: 32,
                                  '&:hover': { 
                                    bgcolor: '#d32f2f',
                                    transform: 'scale(1.1)',
                                  },
                                  transition: 'all 0.2s'
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
                labelDisplayedRows={({ from, to, count }) => 
                  `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
                }
                sx={{
                  borderTop: '1px solid #e0e0e0',
                }}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Modal de importación de ramos */}
      <Dialog 
        open={openImportar} 
        onClose={() => setOpenImportar(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap:1 }}>
            <CloudUploadIcon sx={{ color: 'primary.main', fontSize: 30, mr: 1 }} />
            <Typography variant="h6" fontWeight={700}>Importar Ramos</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box>
            <Box sx={{ border: '2px dashed #ccc', borderRadius: 3, mb: 3, p: 3, background: '#fafbfd' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb:1 }}>
                <CloudDownloadIcon color="primary" sx={{ fontSize: 30, mr: 1 }} />
                <Typography component="span" fontWeight={700} fontSize={18}>1. Descargar Plantilla</Typography>
              </Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Descarga la plantilla Excel con el formato correcto para importar ramos.
              </Typography>
              <Button variant="contained" color="info" fullWidth sx={{ fontWeight: 700, py: 1.2, mb: 1 }} startIcon={<CloudDownloadIcon />} onClick={handleDescargarPlantilla}>
                Descargar Plantilla
              </Button>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ border: '2px dashed #ccc', borderRadius: 3, p: 3, background: '#fafbfd' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb:1 }}>
                <CloudUploadIcon color="primary" sx={{ fontSize: 30, mr: 1 }} />
                <Typography component="span" fontWeight={700} fontSize={18}>2. Importar Plantilla</Typography>
              </Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Selecciona tu archivo Excel completado para importar los ramos.
              </Typography>
              <label htmlFor="excel-upload-productos-desde-ramos">
                <input
                  id="excel-upload-productos-desde-ramos"
                  type="file"
                  accept=".xlsx,.xls"
                  style={{ display: 'none' }}
                  onChange={onImportChange}
                />
                <Button variant="contained" fullWidth color="primary" startIcon={<CloudUploadIcon />} component="span" sx={{ fontWeight: 700, py: 1.2 }}>
                  Seleccionar Archivo
                </Button>
              </label>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenImportar(false)} color="inherit">Cancelar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Ramos; 